import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import VulnFilterBar from '../../components/vuln/VulnFilterBar';
import vulnApi from '../../services/vulnApi';

/**
 * Attack surface: what changed, what is out there, and what we are willing to
 * assert about it.
 *
 * The change feed leads because it is the deliverable. Nobody pays monthly for
 * an inventory of twenty-six subdomains; they pay for "this appeared last week
 * and was not there before". It is also the safest thing to put in front of a
 * customer — every row is true or false with no severity judgment, and severity
 * judgment is exactly where the agent pipeline broke.
 *
 * Claims are a separate tab and show their verdicts, refutations included. A
 * refuted claim is not noise to hide: it is the evidence that verification is
 * doing something, and the corpus that will say whether a change helped.
 */

const CHANGE_TONE = {
  name_appeared: { dot: '#f59e0b', label: 'New name' },
  name_resolves: { dot: '#f59e0b', label: 'Started resolving' },
  service_appeared: { dot: '#dc2626', label: 'New service' },
  address_changed: { dot: '#818cf8', label: 'Address changed' },
  certificate_replaced: { dot: '#818cf8', label: 'Certificate replaced' },
  mail_policy_changed: { dot: '#818cf8', label: 'Mail policy changed' },
  name_stopped_resolving: { dot: '#64748b', label: 'Stopped resolving' },
  service_disappeared: { dot: '#64748b', label: 'Service gone' },
  name_not_observed: { dot: '#64748b', label: 'Not checked' },
  baseline_established: { dot: '#22c55e', label: 'Baseline' },
};

const VERDICT_TONE = {
  confirmed: 'text-red-400',
  refuted: 'text-emerald-400',
  undetermined: 'text-amber-400',
  unverifiable: 'text-slate-500',
  unverified: 'text-slate-500',
};

const TABS = [
  { id: 'changes', label: 'Changes' },
  { id: 'surface', label: 'Surface' },
  { id: 'claims', label: 'Claims' },
  { id: 'runs', label: 'Runs' },
];

function AttackSurface() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [tab, setTab] = useState('changes');
  const [changes, setChanges] = useState([]);
  const [names, setNames] = useState([]);
  const [claims, setClaims] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) {
      setChanges([]); setNames([]); setClaims([]); setRuns([]);
      return;
    }
    setLoading(true);
    try {
      const [c, n, cl, r] = await Promise.all([
        vulnApi.getAsmChanges(customerId, { sinceDays: 90 }),
        vulnApi.getAsmNames(customerId),
        vulnApi.getAsmClaims(customerId),
        vulnApi.getAsmRuns(customerId),
      ]);
      setChanges(c); setNames(n); setClaims(cl); setRuns(r);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (changeId) => {
    await vulnApi.acknowledgeAsmChange(changeId);
    setChanges((prev) => prev.map((c) =>
      (c.id === changeId ? { ...c, acknowledged: true } : c)));
  };

  const latestRun = runs[0];
  const unacknowledged = changes.filter((c) => !c.acknowledged).length;
  const resolving = names.filter((n) => n.resolves).length;
  const alive = names.filter((n) => n.alive).length;
  const confirmed = claims.filter((c) => c.status === 'confirmed').length;
  const refuted = claims.filter((c) => c.status === 'refuted').length;

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-[90rem] mx-auto">

            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-100">Attack Surface</h1>
                <p className="text-sm text-slate-500 mt-1">
                  What is reachable, and what moved since the last run.
                </p>
              </div>
              <VulnFilterBar customerId={customerId} onCustomerChange={setCustomerId} />
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-950/30 border border-red-500/40 text-xs text-slate-300">
                {error}
              </div>
            )}

            {!customerId ? (
              <div className="text-center py-24 text-sm text-slate-500">
                Pick a customer to see their surface.
              </div>
            ) : (
              <>
                {/* A run with failed collectors did not observe everything, so
                    its delta cannot be read as "nothing changed". Saying so is
                    the difference between a quiet week and broken collection. */}
                {latestRun?.failed_collectors?.length > 0 && (
                  <div className="mb-6 p-3 rounded-lg bg-amber-950/20 border border-amber-500/30 text-xs text-amber-200/80">
                    The last run had {latestRun.failed_collectors.length} collector
                    failure{latestRun.failed_collectors.length === 1 ? '' : 's'}
                    {' '}({latestRun.failed_collectors.slice(0, 4).join(', ')}
                    {latestRun.failed_collectors.length > 4 ? '…' : ''}).
                    Its results are incomplete — an absence here is not evidence of absence.
                  </div>
                )}

                <div className="flex flex-wrap items-start gap-x-10 gap-y-3 px-4 py-4 rounded-lg bg-slate-900/50 border border-slate-800 mb-6">
                  <Stat value={unacknowledged} label="unreviewed changes"
                        tone={unacknowledged ? 'text-amber-400' : 'text-slate-100'} />
                  <Stat value={names.length} label="names known" />
                  <Stat value={resolving} label="resolving" />
                  <Stat value={alive} label="answering" />
                  <Stat value={confirmed} label="confirmed claims"
                        tone={confirmed ? 'text-red-400' : 'text-slate-100'} />
                  <Stat value={refuted} label="refuted before publication"
                        tone="text-emerald-400"
                        hint="Claims a deterministic check contradicted. These never reach a customer." />
                  {latestRun && (
                    <div className="ml-auto text-right text-[11px] text-slate-500">
                      Last run {latestRun.finished_at
                        ? new Date(latestRun.finished_at).toLocaleString()
                        : latestRun.status}
                      {latestRun.stale && (
                        <span className="text-amber-400"> · stopped reporting</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-1 mb-4 border-b border-slate-800">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                        tab === t.id
                          ? 'border-indigo-500 text-slate-200'
                          : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {loading ? (
                  <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
                  </div>
                ) : tab === 'changes' ? (
                  <ChangeFeed changes={changes} onAcknowledge={acknowledge} />
                ) : tab === 'surface' ? (
                  <SurfaceTable names={names} />
                ) : tab === 'claims' ? (
                  <ClaimsTable claims={claims} />
                ) : (
                  <RunsTable runs={runs} />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

const Stat = ({ value, label, tone = 'text-slate-100', hint }) => (
  <div title={hint}>
    <div className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

function ChangeFeed({ changes, onAcknowledge }) {
  if (!changes.length) {
    return (
      <div className="text-center py-20 text-sm text-slate-500">
        Nothing has changed in this period.
        <p className="text-xs text-slate-600 mt-1 max-w-md mx-auto">
          On a surface that is genuinely static this is the expected result. Check
          the last run completed before reading it as good news.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {changes.map((change) => {
        const tone = CHANGE_TONE[change.change_type]
          || { dot: '#64748b', label: change.change_type };
        return (
          <div
            key={change.id}
            className={`rounded-lg border border-slate-800 bg-slate-900/40 p-3 ${
              change.acknowledged ? 'opacity-50' : ''}`}
          >
            <div className="flex items-start gap-3">
              <span className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                    style={{ backgroundColor: tone.dot }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm text-slate-100">{change.subject}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {tone.label}
                  </span>
                  <span className="text-[10px] text-slate-600">
                    {new Date(change.observed_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{change.summary}</p>
              </div>
              {!change.acknowledged && (
                <button
                  onClick={() => onAcknowledge(change.id)}
                  className="text-[11px] text-slate-500 hover:text-slate-300 shrink-0"
                >
                  Reviewed
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SurfaceTable({ names }) {
  if (!names.length) {
    return <p className="text-sm text-slate-500 py-10 text-center">No names discovered yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
          <tr>
            <th className="text-left font-medium py-2 px-3">Name</th>
            <th className="text-left font-medium py-2 px-3">Source</th>
            <th className="text-left font-medium py-2 px-3">Resolves</th>
            <th className="text-left font-medium py-2 px-3">Answers</th>
            <th className="text-left font-medium py-2 px-3">Addresses</th>
            <th className="text-left font-medium py-2 px-3">First seen</th>
          </tr>
        </thead>
        <tbody>
          {names.map((n) => (
            <tr key={n.id} className="border-t border-slate-800/60">
              <td className="py-2 px-3 text-slate-200">{n.name}</td>
              <td className="py-2 px-3 text-[11px] text-slate-500">{n.source}</td>
              {/* Null is not false. A name nobody could check is not a name
                  that does not exist, and showing them the same way is how a
                  DNS outage turns into a page of confident negatives. */}
              <td className="py-2 px-3 text-xs">
                {n.resolves === null ? <span className="text-slate-600">not checked</span>
                  : n.resolves ? <span className="text-emerald-400">yes</span>
                  : <span className="text-slate-500">no</span>}
              </td>
              <td className="py-2 px-3 text-xs">
                {n.alive === null ? <span className="text-slate-600">—</span>
                  : n.alive ? <span className="text-amber-400">yes</span>
                  : <span className="text-slate-500">no</span>}
              </td>
              <td className="py-2 px-3 text-[11px] text-slate-500 max-w-xs truncate">
                {(n.addresses || []).join(', ') || '—'}
              </td>
              <td className="py-2 px-3 text-[11px] text-slate-500">
                {n.first_seen ? new Date(n.first_seen).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClaimsTable({ claims }) {
  if (!claims.length) {
    return <p className="text-sm text-slate-500 py-10 text-center">No claims yet.</p>;
  }
  return (
    <div className="space-y-2">
      {claims.map((claim) => (
        <div key={claim.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-xs font-semibold uppercase tracking-wider ${
              VERDICT_TONE[claim.status] || 'text-slate-500'}`}>
              {claim.status}
            </span>
            <span className="text-sm text-slate-200">{claim.subject}</span>
            <span className="text-[11px] text-slate-500">{claim.claim_type}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-600">
              {claim.source}
            </span>
          </div>
          {/* The reason, always. A verdict nobody can audit is a verdict
              nobody should act on — which is where this started. */}
          <p className="text-xs text-slate-400 mt-1">{claim.verdict_reason}</p>
        </div>
      ))}
    </div>
  );
}

function RunsTable({ runs }) {
  if (!runs.length) {
    return <p className="text-sm text-slate-500 py-10 text-center">No runs yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
          <tr>
            <th className="text-left font-medium py-2 px-3">Started</th>
            <th className="text-left font-medium py-2 px-3">Status</th>
            <th className="text-right font-medium py-2 px-3">Names</th>
            <th className="text-right font-medium py-2 px-3">Confirmed</th>
            <th className="text-right font-medium py-2 px-3">Refuted</th>
            <th className="text-right font-medium py-2 px-3">Undetermined</th>
            <th className="text-right font-medium py-2 px-3">Changes</th>
            <th className="text-left font-medium py-2 px-3">Gaps</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const counts = run.counts || {};
            return (
              <tr key={run.id} className="border-t border-slate-800/60">
                <td className="py-2 px-3 text-[11px] text-slate-400">
                  {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                </td>
                <td className="py-2 px-3 text-xs">
                  <span className={
                    run.status === 'completed' ? 'text-emerald-400'
                    : run.status === 'failed' ? 'text-red-400'
                    : run.stale ? 'text-amber-400' : 'text-slate-400'}>
                    {run.stale ? 'stopped reporting' : run.status}
                  </span>
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-slate-300">{counts.names ?? '—'}</td>
                <td className="py-2 px-3 text-right tabular-nums text-red-400">{counts.confirmed ?? '—'}</td>
                <td className="py-2 px-3 text-right tabular-nums text-emerald-400">{counts.refuted ?? '—'}</td>
                <td className="py-2 px-3 text-right tabular-nums text-amber-400">{counts.undetermined ?? '—'}</td>
                <td className="py-2 px-3 text-right tabular-nums text-slate-300">{counts.changes ?? '—'}</td>
                <td className="py-2 px-3 text-[11px] text-slate-500">
                  {run.failed_collectors?.length
                    ? <span className="text-amber-400">{run.failed_collectors.length}</span>
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AttackSurface;
